import { ExternalLinkIcon, InfoIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Link,
  Skeleton,
  SkeletonCircle,
  Text,
  VStack,
} from "@chakra-ui/react";
import { LocalDateTime, ZoneOffset } from "@js-joda/core";
import { useQueries } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Messages } from "../../../core/api";
import ErrorBox from "../../../shared/components/ErrorBox";
import useApi from "../../../shared/hooks/useApi";
import { queryKeys } from "../../../shared/query-keys";
import {
  AgencyMemberId,
  CaregiverId,
  CommCenterTicketId,
  PatientId,
} from "../../../shared/schema/schema";
import { dateFormatter } from "../../../shared/utils/date-formatter";
import CommunicationLogCallDetails from "../../communication/components/communication-log/CommunicationLogCallDetails";
import CommunicationLogEmailDetails from "../../communication/components/communication-log/CommunicationLogEmailDetails";
import CommunicationLogMobileChatDetails from "../../communication/components/communication-log/CommunicationLogMobileChatDetails";
import CommunicationLogSmsDetails from "../../communication/components/communication-log/CommunicationLogSmsDetails";
import { useCommCenterTicketPopup } from "../../communication/hooks/useCommCenterTicketPopup";
import { isEmailTicket } from "../../communication/utils/comm-center-email-utils";
import {
  isCallTicket,
  isMobileChatTicket,
  isSmsTicket,
} from "../../communication/utils/communication-utils";

interface Props {
  patientId: PatientId | null;
  onClose: () => void;
}

export default function PatientTaskActivityDrawer(props: Props) {
  return (
    <Drawer isOpen={props.patientId !== null} size="md" onClose={props.onClose}>
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">Patient Task Activity</DrawerHeader>

        <DrawerBody>
          {props.patientId && <PatientTaskActivityDrawerContent patientId={props.patientId} />}
        </DrawerBody>
      </DrawerContent>
      <DrawerOverlay />
    </Drawer>
  );
}

function combineTimelineEvents(
  taskActivityData: ActivityRecord[],
  communicationData: { tickets: Messages["CommCenterTicket"][] }
): TimelineEvent[] {
  const taskEvents: TimelineEvent[] = taskActivityData.map((record) => ({
    type: "TASK_ACTIVITY" as const,
    timestamp: record.updatedAt,
    data: record,
  }));

  const communicationEvents: TimelineEvent[] = communicationData
    ? communicationData.tickets
        .map((ticket) => {
          const messages =
            ticket.callInfo !== null
              ? [{ createdAt: ticket.createdAt }]
              : ticket.emailThread !== null
              ? ticket.emailThread.messages
              : ticket.messages;

          return messages.map((message, messageIndex) => ({
            type: "COMMUNICATION" as const,
            timestamp: LocalDateTime.ofInstant(message.createdAt, ZoneOffset.UTC),
            data: { ticket, message, messageIndex },
          }));
        })
        .flat()
    : [];

  const allEvents = [...taskEvents, ...communicationEvents];

  return allEvents.sort((a, b) => b.timestamp.compareTo(a.timestamp));
}

function PatientTaskActivityDrawerContent(props: { patientId: PatientId }) {
  const { api, queries } = useApi();
  const ticketPopup = useCommCenterTicketPopup();
  const [showCommunications, setShowCommunications] = useState(false);

  const [taskActivityQuery, communicationQuery] = useQueries({
    queries: [
      {
        queryKey: queryKeys.patient.activity(props.patientId),
        queryFn: () =>
          api.get("./patients/:patientId/task_history_summary", {
            path: { patientId: props.patientId },
          }),
        select: ({ summary }: { summary: ActivityRecord[] }) => summary,
        enabled: props.patientId !== null,
      },
      {
        queryKey: queries.patient.communicationLog(props.patientId).queryKey,
        queryFn: queries.patient.communicationLog(props.patientId).queryFn,
        select: queries.patient.communicationLog(props.patientId).select,
        enabled: props.patientId !== null && showCommunications,
      },
    ],
  });

  const handleClickTicket = (ticketId: CommCenterTicketId) => {
    ticketPopup.open({
      primaryEntity: "Patient",
      patientId: props.patientId,
      defaultTicketId: ticketId,
      contactDetails: null,
    });
  };

  if (taskActivityQuery.isLoading || communicationQuery.isLoading) {
    return (
      <VStack align="stretch" pt={4} spacing={6}>
        {[...Array(5)].map((_, i) => (
          <ActivityRecordSkeleton key={i} />
        ))}
      </VStack>
    );
  }

  if (
    taskActivityQuery.status === "error" ||
    communicationQuery.status === "error" ||
    !taskActivityQuery.data
  ) {
    const errorQuery =
      taskActivityQuery.status === "error" ? taskActivityQuery : communicationQuery;
    return <ErrorBox error={errorQuery.error} resetErrorBoundary={errorQuery.refetch} />;
  }

  return (
    <PatientTaskActivityContent
      communicationData={communicationQuery.data}
      showCommunications={showCommunications}
      taskActivityData={taskActivityQuery.data}
      onClickTicket={handleClickTicket}
      onToggleCommunications={setShowCommunications}
    />
  );
}

interface PatientTaskActivityContentProps {
  taskActivityData: ActivityRecord[];
  communicationData?: { tickets: Messages["CommCenterTicket"][] };
  showCommunications: boolean;
  onToggleCommunications: (show: boolean) => void;
  onClickTicket: (ticketId: CommCenterTicketId) => void;
}

function PatientTaskActivityContent(props: PatientTaskActivityContentProps) {
  const {
    taskActivityData,
    communicationData,
    showCommunications,
    onToggleCommunications,
    onClickTicket,
  } = props;

  const filteredEvents: TimelineEvent[] = useMemo(() => {
    if (!communicationData || !showCommunications) {
      return combineTimelineEvents(taskActivityData, { tickets: [] });
    }

    return combineTimelineEvents(taskActivityData, communicationData);
  }, [taskActivityData, communicationData, showCommunications]);

  if (filteredEvents.length === 0) {
    return <EmptyActivityState />;
  }

  return (
    <VStack align="stretch" pt={4} spacing={4}>
      <Box bg="gray.50" borderRadius="md" p={3}>
        <Checkbox
          colorScheme="blue"
          isChecked={showCommunications}
          onChange={(e) => onToggleCommunications(e.target.checked)}
        >
          <Text fontSize="sm" fontWeight="medium">
            Show communication events
          </Text>
        </Checkbox>
      </Box>

      {filteredEvents.map((event) => {
        return (
          <TimelineEventItem
            key={generateEventKey(event)}
            event={event}
            onClickTicket={onClickTicket}
          />
        );
      })}
    </VStack>
  );
}

function isTaskActivityEvent(event: TimelineEvent): event is TaskActivityEvent {
  return event.type === "TASK_ACTIVITY";
}

function isCommunicationEvent(event: TimelineEvent): event is CommunicationEvent {
  return event.type === "COMMUNICATION";
}

function getEventTypeDisplay(event: TimelineEvent) {
  switch (event.type) {
    case "TASK_ACTIVITY":
      return { label: "Task Activity", colorScheme: "blue" as const };
    case "COMMUNICATION":
      return { label: "Communication", colorScheme: "purple" as const };
  }
}

function TimelineEventItem(props: {
  event: TimelineEvent;
  onClickTicket: (ticketId: CommCenterTicketId) => void;
}) {
  const { event, onClickTicket } = props;

  const { label, colorScheme } = getEventTypeDisplay(event);

  return (
    <Box bg="white" borderRadius="lg" borderWidth="1px" p={4} shadow="sm">
      <Flex direction="column" gap={3}>
        <Flex alignItems="center" justifyContent="space-between">
          <Badge colorScheme={colorScheme} px={3} py={1} variant="subtle">
            {label}
          </Badge>
          <Text color="gray.500" fontSize="sm" fontWeight="medium">
            {dateFormatter.toDateTime(event.timestamp)}
          </Text>
        </Flex>

        {isTaskActivityEvent(event) ? (
          <TaskActivityContent record={event.data} />
        ) : isCommunicationEvent(event) ? (
          <CommunicationContent event={event} onClickTicket={onClickTicket} />
        ) : null}
      </Flex>
    </Box>
  );
}

function CommunicationContent(props: {
  event: CommunicationEvent;
  onClickTicket: (ticketId: CommCenterTicketId) => void;
}) {
  const { event, onClickTicket } = props;
  const { ticket } = event.data;

  const getCommunicationType = () => {
    if (isCallTicket(ticket)) return "Call";
    if (isEmailTicket(ticket)) return "Email";
    if (isSmsTicket(ticket)) return "SMS";
    if (isMobileChatTicket(ticket)) return "Mobile Chat";
    return "Communication";
  };

  return (
    <VStack align="start" spacing={3}>
      <Flex alignItems="center" flexWrap="wrap" gap={2}>
        <Badge
          colorScheme={getCommunicationStatusColor(ticket.status)}
          fontSize="xs"
          px={2}
          py={1}
          variant="subtle"
        >
          {getCommunicationStatus(ticket.status)}
        </Badge>
        <Badge colorScheme="gray" fontSize="xs" px={2} py={1} variant="outline">
          {getCommunicationType()}
        </Badge>
        <Badge colorScheme="purple" fontSize="xs" px={2} py={1} variant="outline">
          #{ticket.id}
        </Badge>
      </Flex>

      <Box w="full">
        <CommunicationDetails ticket={ticket} />
      </Box>

      <Link color="blue.600" fontSize="sm" onClick={() => onClickTicket(ticket.id)}>
        <Flex alignItems="center">
          View ticket
          <ExternalLinkIcon mx={1} />
        </Flex>
      </Link>
    </VStack>
  );
}

function TaskActivityContent(props: { record: ActivityRecord }) {
  const { record } = props;
  const { actor, summary } = record;
  const { name, avatar, isSystem } = getActorDisplay(actor);

  return (
    <HStack align="flex-start" gap={4}>
      <ActorAvatar avatar={avatar} isSystem={isSystem} name={name} />
      <VStack align="start" flex={1} spacing={3}>
        <Text color="gray.800" fontSize="md" fontWeight="semibold">
          {name}
        </Text>

        <Box w="full">
          <Text fontSize="sm">{summary}</Text>
        </Box>
      </VStack>
    </HStack>
  );
}

function ActorAvatar({ name, avatar }: { name: string; avatar: string | null; isSystem: boolean }) {
  return <Avatar name={name} src={avatar || undefined} />;
}

function ActivityRecordSkeleton() {
  return (
    <Box bg="white" borderRadius="lg" borderWidth="1px" p={4} shadow="sm">
      <Flex direction="column" gap={3}>
        <Flex alignItems="center" justifyContent="space-between">
          <Skeleton height="24px" width="120px" />
          <Skeleton height="16px" width="140px" />
        </Flex>

        <HStack align="flex-start" gap={4}>
          <SkeletonCircle size="12" />
          <VStack align="start" flex={1} spacing={3}>
            <Skeleton height="20px" width="180px" />
            <Skeleton height="16px" width="260px" />
          </VStack>
        </HStack>
      </Flex>
    </Box>
  );
}

function EmptyActivityState() {
  return (
    <VStack align="center" color="gray.400" justify="center" py={12} spacing={3}>
      <InfoIcon boxSize={8} />
      <Text fontSize="lg" fontWeight="medium">
        No activity found for this patient.
      </Text>
    </VStack>
  );
}

function generateEventKey(event: TimelineEvent): string {
  const timestamp = event.timestamp.toString();

  switch (event.type) {
    case "TASK_ACTIVITY": {
      return `task-${timestamp}`;
    }

    case "COMMUNICATION": {
      return `comm-${event.data.ticket.id}-${event.data.messageIndex}`;
    }
  }
}

function getActorDisplay(actor: ActivityActor | null): {
  name: string;
  avatar: string | null;
  isSystem: boolean;
} {
  if (actor === null) {
    return { name: "Unknown", avatar: null, isSystem: false };
  }

  switch (actor.type) {
    case "AGENCY_MEMBER":
    case "CAREGIVER":
      return { name: actor.name, avatar: actor.avatar, isSystem: false };
    case "SYSTEM":
      return { name: "System", avatar: null, isSystem: true };
  }
}

function getCommunicationStatus(status: string): string {
  return status === "RESOLVED" ? "Resolved" : "Ongoing";
}

function getCommunicationStatusColor(status: string): "green" | "blue" {
  return status === "RESOLVED" ? "green" : "blue";
}

interface CommunicationDetailsProps {
  ticket: Messages["CommCenterTicket"];
}

function CommunicationDetails({ ticket }: CommunicationDetailsProps) {
  if (isCallTicket(ticket)) {
    return <CommunicationLogCallDetails ticket={ticket} />;
  }
  if (isSmsTicket(ticket)) {
    return <CommunicationLogSmsDetails ticket={ticket} />;
  }
  if (isEmailTicket(ticket)) {
    return <CommunicationLogEmailDetails ticket={ticket} />;
  }
  if (isMobileChatTicket(ticket)) {
    return <CommunicationLogMobileChatDetails ticket={ticket} />;
  }
  return <Text>Communication event</Text>;
}

type ActivityActor =
  | {
      type: "CAREGIVER";
      id: CaregiverId;
      name: string;
      avatar: string | null;
    }
  | {
      type: "AGENCY_MEMBER";
      id: AgencyMemberId;
      name: string;
      avatar: string | null;
    }
  | { type: "SYSTEM" };

interface ActivityRecord {
  updatedAt: LocalDateTime;
  actor: ActivityActor | null;
  summary: string;
}

interface BaseTimelineEvent {
  type: string;
  timestamp: LocalDateTime;
}

interface TaskActivityEvent extends BaseTimelineEvent {
  type: "TASK_ACTIVITY";
  data: ActivityRecord;
}

interface CommunicationEvent extends BaseTimelineEvent {
  type: "COMMUNICATION";
  data: {
    ticket: Messages["CommCenterTicket"];
    message: any;
    messageIndex: number;
  };
}

type TimelineEvent = TaskActivityEvent | CommunicationEvent;
